/**
 * Admin Utilities - Functions for handling admin, test, and demo user privileges
 * This file provides utilities to check if users have admin privileges and bypass restrictions
 */

// Check if user has admin privileges (isAdmin, testUser, or demoUser = true)
export const hasAdminPrivileges = (currentUserData) => {
  const isAdmin = currentUserData?.isAdmin === true
  const isTestUser = currentUserData?.testUser === true
  const isDemoUser = currentUserData?.demoUser === true
  const hasPrivileges = isAdmin || isTestUser || isDemoUser
  
  return hasPrivileges
}

// Check if user is specifically an admin (not test or demo)
export const isAdmin = (currentUserData) => {
  return currentUserData?.isAdmin === true
}

// Check if user is a test user
export const isTestUser = (currentUserData) => {
  return currentUserData?.testUser === true
}

// Check if user is a demo user
export const isDemoUser = (currentUserData) => {
  return currentUserData?.demoUser === true
}

// Get admin type for display purposes
export const getAdminType = (currentUserData) => {
  // Check in priority order: Admin > Test User > Demo User
  if (currentUserData?.isAdmin === true) return 'Admin'
  if (currentUserData?.testUser === true) return 'Test User'
  if (currentUserData?.demoUser === true) return 'Demo User'
  return null
}

// Check if user can bypass all restrictions
export const canBypassRestrictions = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can access all features
export const canAccessAllFeatures = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can use unlimited resources
export const hasUnlimitedResources = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Get admin badge component props
export const getAdminBadgeProps = (currentUserData) => {
  if (!hasAdminPrivileges(currentUserData)) {
    return null
  }

  const adminType = getAdminType(currentUserData)
  
  const badgeStyles = {
    Admin: {
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-200'
    },
    'Test User': {
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-200'
    },
    'Demo User': {
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-200'
    }
  }

  return {
    label: adminType,
    ...badgeStyles[adminType]
  }
}

// Check if user should see admin features
export const shouldShowAdminFeatures = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can manage other users (admin only)
export const canManageUsers = (currentUserData) => {
  return isAdmin(currentUserData)
}

// Check if user can access system settings (admin only)
export const canAccessSystemSettings = (currentUserData) => {
  return isAdmin(currentUserData)
}

// Check if user can view analytics (admin and test users)
export const canViewAnalytics = (currentUserData) => {
  return isAdmin(currentUserData) || isTestUser(currentUserData)
}

// Check if user can export data (admin and test users)
export const canExportData = (currentUserData) => {
  return isAdmin(currentUserData) || isTestUser(currentUserData)
}

// Get admin-specific limits (returns Infinity for admin users)
export const getAdminLimit = (currentUserData, defaultLimit) => {
  return hasAdminPrivileges(currentUserData) ? Infinity : defaultLimit
}

// Check if user can use premium features
export const canUsePremiumFeatures = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can use beta features
export const canUseBetaFeatures = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can access developer tools
export const canAccessDeveloperTools = (currentUserData) => {
  return isAdmin(currentUserData) || isTestUser(currentUserData)
}

// Check if user can bypass rate limits
export const canBypassRateLimits = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can access debug information
export const canAccessDebugInfo = (currentUserData) => {
  return isAdmin(currentUserData) || isTestUser(currentUserData)
}

// Get admin-specific configuration
export const getAdminConfig = (currentUserData) => {
  if (!hasAdminPrivileges(currentUserData)) {
    return {}
  }

  return {
    unlimitedDevices: true,
    unlimitedScreens: true,
    unlimitedSchedules: true,
    unlimitedStorage: true,
    premiumFeatures: true,
    betaFeatures: true,
    debugMode: isAdmin(currentUserData) || isTestUser(currentUserData),
    developerTools: isAdmin(currentUserData) || isTestUser(currentUserData),
    analyticsAccess: isAdmin(currentUserData) || isTestUser(currentUserData),
    exportData: isAdmin(currentUserData) || isTestUser(currentUserData),
    systemSettings: isAdmin(currentUserData),
    userManagement: isAdmin(currentUserData)
  }
}

// Check if user should see upgrade prompts
export const shouldShowUpgradePrompts = (currentUserData) => {
  return !hasAdminPrivileges(currentUserData)
}

// Check if user should see subscription messages
export const shouldShowSubscriptionMessages = (currentUserData) => {
  return !hasAdminPrivileges(currentUserData)
}

// Get admin-specific message (for admin users, no restrictions apply)
export const getAdminMessage = (currentUserData) => {
  if (!hasAdminPrivileges(currentUserData)) {
    return null
  }

  const adminType = getAdminType(currentUserData)
  return `${adminType} privileges active - All restrictions bypassed`
}

// Check if user can access AI features without restrictions
export const canUseAIWithoutRestrictions = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can use dynamic content generation without limits
export const canUseDynamicContentWithoutLimits = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Check if user can access all API features
export const canAccessAllAPIFeatures = (currentUserData) => {
  return hasAdminPrivileges(currentUserData)
}

// Get admin-specific feature flags
export const getAdminFeatureFlags = (currentUserData) => {
  if (!hasAdminPrivileges(currentUserData)) {
    return {}
  }

  return {
    unlimitedContent: true,
    unlimitedAPI: true,
    unlimitedStorage: true,
    premiumTemplates: true,
    advancedAnalytics: true,
    customIntegrations: true,
    prioritySupport: true,
    earlyAccess: true,
    betaFeatures: true,
    debugMode: isAdmin(currentUserData) || isTestUser(currentUserData)
  }
} 